import React, { useState, useRef, useCallback } from 'react';
import { profilesApi } from '../../api';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbModal from '../controls/HrnbModal';
import '../../css/components/ProfileCoverUploader.css';

// Максимальный размер файла: 10MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const ProfileCoverUploader = ({ profileId, currentCoverUrl, onCoverUpdate }) => {
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);

    // Обработка выбора файла
    const handleFileSelect = useCallback((e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Проверка типа файла
        if (!ALLOWED_TYPES.includes(file.type)) {
            setError('Неподдерживаемый формат файла. Используйте JPEG, PNG или WebP');
            return;
        }

        // Проверка размера
        if (file.size > MAX_FILE_SIZE) {
            setError('Размер файла не должен превышать 10MB');
            return;
        }

        setError(null);
        setSelectedFile(file);

        // Предпросмотр
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
    }, []);

    // Открытие модального окна
    const handleOpenModal = () => {
        setUploadModalOpen(true);
        setPreviewUrl(null);
        setSelectedFile(null);
        setError(null);
        setUploadProgress(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Закрытие модального окна
    const handleCloseModal = () => {
        setUploadModalOpen(false);
        setPreviewUrl(null);
        setSelectedFile(null);
        setError(null);
        setUploadProgress(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Загрузка обложки
    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Выберите файл для загрузки');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            const result = await profilesApi.uploadCover(profileId, formData, (progressEvent) => {
                const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(progress);
            });

            // Уведомляем родительский компонент об обновлении
            if (onCoverUpdate) {
                onCoverUpdate(result.coverUrl || result.coverImageUrl);
            }

            handleCloseModal();
        } catch (err) {
            console.error('[ProfileCoverUploader] Failed to upload cover:', err.message);
            if (err.response?.status === 413) {
                setError('Файл слишком большой. Максимальный размер 10MB');
            } else if (err.response?.status === 415) {
                setError('Неподдерживаемый формат файла');
            } else {
                setError(err.message || 'Не удалось загрузить обложку');
            }
        } finally {
            setLoading(false);
        }
    };

    // Удаление текущей обложки
    const handleRemoveCover = async () => {
        setDeleteModalOpen(false);
        setLoading(true);
        try {
            if (onCoverUpdate) {
                onCoverUpdate(null);
            }
        } catch (err) {
            console.error('[ProfileCoverUploader] Failed to remove cover:', err.message);
            setError('Не удалось удалить обложку');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="profile-cover-uploader">
                <div className="profile-cover-preview">
                    {currentCoverUrl ? (
                        <img
                            src={currentCoverUrl}
                            alt="Обложка профиля"
                            className="profile-cover-image"
                        />
                    ) : (
                        <div className="profile-cover-placeholder">
                            <span className="profile-cover-placeholder-icon">🖼️</span>
                            <p>Обложка не загружена</p>
                        </div>
                    )}

                    <div className="profile-cover-actions">
                        <HrnbButton
                            variant="primary"
                            onClick={handleOpenModal}
                            disabled={loading}
                        >
                            {currentCoverUrl ? 'Изменить обложку' : 'Загрузить обложку'}
                        </HrnbButton>
                        {currentCoverUrl && (
                            <HrnbButton
                                variant="outline-danger"
                                onClick={() => setDeleteModalOpen(true)}
                                disabled={loading}
                            >
                                Удалить
                            </HrnbButton>
                        )}
                    </div>
                </div>

                <div className="profile-cover-requirements">
                    <h4 className="profile-cover-requirements-title">Требования к обложке:</h4>
                    <ul className="profile-cover-requirements-list">
                        <li>Форматы: JPEG, PNG, WebP</li>
                        <li>Максимальный размер: 10MB</li>
                        <li>Рекомендуемые размеры: 1920x400px</li>
                        <li>Минимальные размеры: 800x200px</li>
                    </ul>
                </div>
            </div>

            {/* Модальное окно загрузки */}
            <HrnbModal
                open={uploadModalOpen}
                onClose={handleCloseModal}
                title="Загрузка обложки"
            >
                <div className="profile-cover-modal">
                    {error && (
                        <HrnbAlert variant="danger" onClose={() => setError(null)} className="mb-3">
                            {error}
                        </HrnbAlert>
                    )}

                    <div className="profile-cover-upload-area">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={handleFileSelect}
                            className="profile-cover-file-input"
                            disabled={loading}
                        />

                        {previewUrl ? (
                            <div className="profile-cover-preview-container">
                                <img
                                    src={previewUrl}
                                    alt="Предпросмотр"
                                    className="profile-cover-preview-image"
                                />
                                <HrnbButton
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => {
                                        setPreviewUrl(null);
                                        setSelectedFile(null);
                                        if (fileInputRef.current) {
                                            fileInputRef.current.value = '';
                                        }
                                    }}
                                    disabled={loading}
                                    className="profile-cover-remove-preview"
                                >
                                    Выбрать другой файл
                                </HrnbButton>
                            </div>
                        ) : (
                            <div
                                className="profile-cover-drop-zone"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <span className="profile-cover-drop-icon">📁</span>
                                <p className="profile-cover-drop-text">
                                    Нажмите для выбора файла или перетащите изображение сюда
                                </p>
                                <p className="profile-cover-drop-hint">
                                    JPEG, PNG, WebP до 10MB
                                </p>
                            </div>
                        )}
                    </div>

                    {uploadProgress > 0 && uploadProgress < 100 && (
                        <div className="profile-cover-progress">
                            <div className="profile-cover-progress-bar">
                                <div
                                    className="profile-cover-progress-fill"
                                    style={{ width: `${uploadProgress}%` }}
                                />
                            </div>
                            <p className="profile-cover-progress-text">{uploadProgress}%</p>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
                        <HrnbButton variant="outline" onClick={handleCloseModal} disabled={loading}>
                            Отмена
                        </HrnbButton>
                        <HrnbButton
                            variant="primary"
                            onClick={handleUpload}
                            disabled={!selectedFile || loading}
                        >
                            {loading ? <HrnbLoader size="sm" /> : 'Загрузить'}
                        </HrnbButton>
                    </div>
                </div>
            </HrnbModal>

            {/* Модальное окно подтверждения удаления */}
            <HrnbModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title="Удаление обложки"
            >
                <p style={{ color: '#e0e0e0' }}>
                    Вы уверены, что хотите удалить обложку профиля?
                </p>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
                    <HrnbButton variant="outline" onClick={() => setDeleteModalOpen(false)} disabled={loading}>
                        Отмена
                    </HrnbButton>
                    <HrnbButton variant="danger" onClick={handleRemoveCover} disabled={loading}>
                        Удалить
                    </HrnbButton>
                </div>
            </HrnbModal>
        </>
    );
};

export default ProfileCoverUploader;
